/**
 * av-dark-scroll.js
 * Shared scroll-triggered dark mode enhancement, used by any Ascari
 * Vintage Racer campaign section that offers a "Switch to dark colors
 * while this section is in view" setting (currently av-mosaic and
 * av-expressions). Only runs on sections that opted in — marked with
 * [data-av-dark-toggle] — a section without the setting is never touched.
 *
 * Deliberately NOT built on IntersectionObserver. An earlier version used
 * IntersectionObserver with custom thresholds, a viewport-coverage
 * calculation, a second "exit guard" observer, and a polling backstop on
 * top of that — and a dark section still got stuck dark after being
 * scrolled fully past. Rather than keep patching an observer-based design
 * whose exact failure mode wasn't reproducible in-editor, this rewrite
 * replaces it with one simple mechanism: on every scroll (and resize),
 * rAF-throttled, every tracked section recomputes its own visibility
 * directly from getBoundingClientRect() and sets its class accordingly.
 * There is no cached "armed" state that can go stale — every frame's
 * answer is derived fresh from the DOM's current geometry, so there is
 * nothing for the browser's IntersectionObserver batching/coalescing
 * behavior to interfere with.
 *
 * What gets measured, per section:
 *  - By default, the section's own root — dark mode arms while the
 *    section itself covers a large enough share of the viewport (this is
 *    what av-mosaic uses).
 *  - If the section contains one or more descendants marked
 *    [data-av-dark-trigger], those are measured instead, and the section
 *    goes dark while ANY of them qualifies — this lets av-expressions tie
 *    the effect to a single row (e.g. only the "after dark" one) instead
 *    of the whole section.
 *
 * Enter/exit are intentionally asymmetric: a target arms once its on-screen
 * share of the viewport reaches the configured threshold, but only
 * releases once it is completely off-screen (zero overlap) — not merely
 * back below the threshold. Without this, a tall trigger row would drop
 * below e.g. 35% coverage well before it's actually finished scrolling
 * past, flipping back to light mid-row.
 *
 * Sticky mode: [data-av-dark-sticky="true"] — once a section goes dark it
 * locks dark for the rest of the page visit and stops being recomputed.
 *
 * Chain-to-next mode: [data-av-dark-chain-next="true"] links a section
 * with the very next section in the page (found via Shopify's standard
 * `#shopify-section-*` wrapper — never a hardcoded id) if that section
 * also has dark mode enabled. A linked pair's displayed state is always
 * `A.local || B.local`, symmetric in both scroll directions, so two
 * adjacent dark sections read as one continuous passage instead of
 * flashing back to light at the boundary between them.
 */
(function () {
  var sections = [];
  var linkPairs = new WeakMap(); // section descriptor -> partner descriptor
  var ticking = false;
  var byElement = new WeakMap(); // DOM element -> section descriptor

  function viewportHeight() {
    return window.innerHeight || document.documentElement.clientHeight || 0;
  }

  function coverage(el) {
    var rect = el.getBoundingClientRect();
    var vh = viewportHeight();
    if (vh <= 0) return 0;
    var visible = Math.min(rect.bottom, vh) - Math.max(rect.top, 0);
    return Math.max(0, visible) / vh;
  }

  function isFullyOffscreen(el) {
    var rect = el.getBoundingClientRect();
    var vh = viewportHeight();
    return rect.bottom <= 0 || rect.top >= vh;
  }

  function findNextDarkToggleElement(el) {
    var wrapper = el.closest('[id^="shopify-section-"]');
    var nextWrapper = wrapper && wrapper.nextElementSibling;
    if (!nextWrapper) return null;
    return nextWrapper.querySelector('[data-av-dark-toggle]');
  }

  function buildSection(el) {
    var thresholdPercent = parseFloat(el.dataset.avDarkThreshold) || 35;
    var enterThreshold = Math.min(Math.max(thresholdPercent / 100, 0), 1);
    var targetEls = Array.from(el.querySelectorAll('[data-av-dark-trigger]'));
    if (!targetEls.length) targetEls = [el];

    return {
      el: el,
      targets: targetEls.map(function (targetEl) {
        return { el: targetEl, armed: false };
      }),
      enterThreshold: enterThreshold,
      sticky: el.dataset.avDarkSticky === 'true',
      chainNext: el.dataset.avDarkChainNext === 'true',
      local: false,
      stickyLocked: false
    };
  }

  function updateSection(section) {
    if (section.stickyLocked) return;

    section.targets.forEach(function (target) {
      if (isFullyOffscreen(target.el)) {
        target.armed = false;
      } else if (coverage(target.el) >= section.enterThreshold) {
        target.armed = true;
      }
      // Otherwise: partially visible but under threshold — hold state.
    });

    var isDark = section.targets.some(function (target) {
      return target.armed;
    });
    section.local = isDark;

    if (section.sticky && isDark) {
      section.stickyLocked = true;
    }
  }

  function render(section) {
    var partner = linkPairs.get(section);
    var partnerDark = partner ? Boolean(partner.local) : false;
    section.el.classList.toggle('is-dark', Boolean(section.local) || partnerDark);
  }

  function tick() {
    ticking = false;
    sections.forEach(updateSection);
    sections.forEach(render);
  }

  function requestTick() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(tick);
  }

  function registerLink(section, partnerEl) {
    if (!partnerEl) return;
    var partner = byElement.get(partnerEl);
    if (!partner || partner === section) return;
    linkPairs.set(section, partner);
    linkPairs.set(partner, section);
  }

  function boot(scope) {
    var found = (scope || document).querySelectorAll('[data-av-dark-toggle]');
    var added = false;

    found.forEach(function (el) {
      if (byElement.has(el)) return;
      var section = buildSection(el);
      byElement.set(el, section);
      sections.push(section);
      added = true;
    });

    if (added) {
      // Link chains after every relevant section descriptor exists, so the
      // lookup by element always succeeds regardless of DOM/init order.
      sections.forEach(function (section) {
        if (section.chainNext && !linkPairs.has(section)) {
          registerLink(section, findNextDarkToggleElement(section.el));
        }
      });
      tick();
    }
  }

  function teardown(scope) {
    var removed = (scope || document).querySelectorAll('[data-av-dark-toggle]');
    removed.forEach(function (el) {
      var section = byElement.get(el);
      if (!section) return;
      var partner = linkPairs.get(section);
      if (partner) {
        linkPairs.delete(partner);
        render(partner);
      }
      linkPairs.delete(section);
      byElement.delete(el);
      sections = sections.filter(function (s) {
        return s !== section;
      });
    });
  }

  window.addEventListener('scroll', requestTick, { passive: true });
  window.addEventListener('resize', requestTick);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    teardown(event.target);
  });
})();


/* mobile scroll dark */

document.addEventListener("DOMContentLoaded", function () {
    const sections = document.querySelectorAll(".av-object-wrapper");
    const visibleSections = new Set();

    if (!sections.length) return;

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            visibleSections.add(entry.target);
          } else {
            visibleSections.delete(entry.target);
          }
        });

        document.body.classList.toggle(
          "av-object-in-view",
          visibleSections.size > 0
        );
      },
      {
        threshold: 0.2
      }
    );

    sections.forEach(function (section) {
      observer.observe(section);
    });
  });